import React, { useCallback, useEffect, useRef, useState } from 'react'
import { SectionCard, Select, type SelectOption } from '@enamelai/design-system'
import { Icon as ShieldIcon } from '@enamelai/design-system/icons/Shield'
import './PatientProfileInsuranceSummary.css'
import { InsurancePlan } from '@dataTypes/insurance.types'
import { GetInsurancePlansByPatientId } from '@services/api/insurance.api'
import { useAppSelector } from '@store/hooks'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { getInsurancePlanSummary } from '@utils/insurance.util'
import {
  mockInsuranceFamilyMembers,
  mockInsurancePlans,
} from '@assets/mockdata/PatientTray/mockInsurance'
import {
  InsurancePlanCard,
  InsurancePlanViewModel,
} from './InsurancePlanCard.component'
import { getFullName } from '@utils/fullname.util'
import ProfileEmptyState from '@pages/PatientProfile/shared/ProfileEmptyState.component'

type PatientProfileInsuranceSummaryProps = {
  patientId: string
  useDemoData?: boolean
  forceEmptyStates?: boolean
}

function PatientProfileInsuranceSummary({
  patientId,
  useDemoData = false,
  forceEmptyStates = false,
}: PatientProfileInsuranceSummaryProps): React.ReactElement {
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const patient = useAppSelector((state) => state.patient.selectedPatient)
  const [familyMembers, setFamilyMembers] = useState<any[]>([])
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const [selectedMemberId, setSelectedMemberId] = useState<string>(
    patient?.patient_id ?? '',
  )
  const membersInsurancePlans = useRef<
    { memberId: string; insurancePlansSummary: InsurancePlanViewModel[] }[]
  >([])
  const [selectedInsurancePlanId, setSelectedInsurancePlanId] =
    useState<string>('')
  const isLoading = useRef<boolean>(false)

  useEffect(() => {
    setSelectedMemberId(patient?.patient_id ?? '')
  }, [patient])

  const normalizeInsurancePlans = (
    memberId: string,
    plans: InsurancePlan[],
  ): void => {
    const plansSummary: InsurancePlanViewModel[] = []
    plans.forEach((plan) => {
      const summary = getInsurancePlanSummary(plan.services ?? [], false)
      plansSummary.push({
        id: plan.insurance_plan_id!,
        payerName: plan.carrier_name!,
        subscriberId: plan.subscriber_member_id!,
        validity:
          (plan as { end_date?: string }).end_date ??
          plan.validity ??
          `12-31-${new Date().getFullYear()}`,
        status: plan.status,
        networks: summary,
      })
    })
    membersInsurancePlans.current.push({
      memberId,
      insurancePlansSummary: plansSummary,
    })
  }

  const fetchData = useCallback(async (): Promise<void> => {
    showLoading({
      container: '.insurance-summary-page',
      hideElements: ['.insurance-plans-summary'],
    })
    try {
      membersInsurancePlans.current = []

      if (useDemoData) {
        const demoPlans = mockInsurancePlans[patientId] || []
        const demoFamily = mockInsuranceFamilyMembers[patientId] || []
        normalizeInsurancePlans(patientId, demoPlans)
        demoFamily.forEach((member) => {
          normalizeInsurancePlans(member.member_id, member.insurancePlans || [])
        })
        if (membersInsurancePlans.current.length > 0) {
          setSelectedInsurancePlanId(
            membersInsurancePlans.current[0]?.insurancePlansSummary?.[0]?.id ??
              '',
          )
        }
        setFamilyMembers(demoFamily)
        hideLoading('.insurance-summary-page')
        return
      }

      const response = await GetInsurancePlansByPatientId(patientId)
      normalizeInsurancePlans(patientId, response?.data?.insurancePlans || [])
      const familyMembersData = response?.data?.familyMembers || []
      familyMembersData.forEach((member) => {
        normalizeInsurancePlans(member.member_id, member.insurancePlans || [])
      })
      if (membersInsurancePlans.current.length > 0) {
        setSelectedInsurancePlanId(
          membersInsurancePlans?.current?.[0]?.insurancePlansSummary?.[0]?.id ??
            '',
        )
      }
      setFamilyMembers(familyMembersData)
      hideLoading('.insurance-summary-page')
    } catch (error) {
      membersInsurancePlans.current = []
      hideLoading('.insurance-summary-page')
      console.error(error)
    }
  }, [selectedClinic, patientId, useDemoData, showLoading, hideLoading])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const getAccountMembers = (): React.ReactElement => {
    const memberOptions: SelectOption[] = []
    if (patient) {
      memberOptions.push({
        value: patient.patient_id,
        label: `${getFullName(patient)} - Subscriber`,
      })
    }
    familyMembers.forEach((member) =>
      memberOptions.push({
        value: member.member_id,
        label: `${getFullName(member)} - Dependent`,
      }),
    )
    return (
      <Select
        className="patient-profile-insurance-select"
        label="Account member"
        options={memberOptions}
        value={selectedMemberId}
        onChange={(value) => {
          isLoading.current = true
          setSelectedMemberId(String(value))
          setTimeout(() => {
            isLoading.current = false
          }, 1000)
        }}
      />
    )
  }

  const getMemberInsurancePlansSummary = (): InsurancePlanViewModel[] => {
    const currentMemberInsurancePlans = membersInsurancePlans.current.find(
      (member) => member.memberId === selectedMemberId,
    )
    return currentMemberInsurancePlans?.insurancePlansSummary || []
  }

  const currentMemberInsurancePlansSummary = forceEmptyStates
    ? []
    : getMemberInsurancePlansSummary()

  const getInsurancePlans = (): React.ReactElement => (
    <Select
      className="patient-profile-insurance-select"
      label="Insurance plan"
      options={currentMemberInsurancePlansSummary.map((plan) => ({
        value: plan.id,
        label: plan.payerName,
      }))}
      value={selectedInsurancePlanId}
      onChange={(value) => setSelectedInsurancePlanId(String(value))}
    />
  )

  const emptyInsuranceElement = (): React.ReactElement => {
    if (isLoading.current) return <></>
    return (
      <div className="insurance-empty-state-wrapper">
        <ProfileEmptyState
          icon={ShieldIcon}
          title="No insurance plans found"
          description="You don't have any insurance plans on record yet."
        />
      </div>
    )
  }

  useEffect(() => {
    const insurancePlanIdFirst =
      currentMemberInsurancePlansSummary?.[0]?.id || ''
    if (selectedInsurancePlanId !== insurancePlanIdFirst) {
      setSelectedInsurancePlanId(insurancePlanIdFirst)
    }
  }, [selectedMemberId])

  return (
    <SectionCard
      className={`patient-profile-section insurance-plans-summary ${currentMemberInsurancePlansSummary.length > 0 ? 'insurance-plan-list' : 'empty-insurance-plan-list'}`}
      title={
        currentMemberInsurancePlansSummary.length > 0
          ? 'Primary dental benefit plan'
          : undefined
      }
      headerRight={
        currentMemberInsurancePlansSummary.length > 0 ||
        familyMembers.length > 0 ? (
          <div className="patient-profile-insurance-selects">
            {getAccountMembers()}
            {currentMemberInsurancePlansSummary.length > 0 &&
              getInsurancePlans()}
          </div>
        ) : undefined
      }
    >
      {selectedInsurancePlanId &&
      currentMemberInsurancePlansSummary.find(
        (plan) => plan.id === selectedInsurancePlanId,
      ) ? (
        <InsurancePlanCard
          key={selectedInsurancePlanId}
          plan={
            currentMemberInsurancePlansSummary.find(
              (plan) => plan.id === selectedInsurancePlanId,
            ) as InsurancePlanViewModel
          }
        />
      ) : (
        emptyInsuranceElement()
      )}
    </SectionCard>
  )
}

export default PatientProfileInsuranceSummary
